import { useEffect, useMemo, useRef, useState } from 'react';
import { bridge } from 'shared/src/utils';
import { type GoodsOption } from 'shared/src/api';
import { useIsAnonymousUser } from 'shared/src/hooks';

import type { ProductOptionMaxDiscountMode } from '../../../hooks/useProductOptionMaxDiscountMode';
import { useGoodsOptionsPricing } from '../queries/useGoodsOptionsPricing';
import { usePostBatchRedeemCoupon } from '../queries/usePostBatchRedeemCoupon';
import { useLogDownloadCoupon } from './useLogCompleteCouponDownload';

export const useManageMaxDiscountCouponAutoRedeem = ({
  mode,
  selected,
}: {
  mode: ProductOptionMaxDiscountMode;
  selected: GoodsOption[];
}): { isAutoRedeeming: boolean } => {
  const isAnonymousUser = useIsAnonymousUser();
  const { data: pricing } = useGoodsOptionsPricing({
    options: selected.map(option => ({
      sno: option.sno,
      count: option.purchase_count ?? 1,
    })),
  });
  const { mutateAsync: postBatchRedeemCoupon, isPending } =
    usePostBatchRedeemCoupon(selected);
  const logCouponDownloadComplete = useLogDownloadCoupon();
  const [isAutoRedeeming, setIsAutoRedeeming] = useState(false);
  const attemptedKeysRef = useRef(new Set<string>());
  const downloadable = useMemo(
    () => pricing?.coupon_list.filter(c => !c.item.is_issued) ?? [],
    [pricing?.coupon_list],
  );
  const redeemKey = useMemo(() => {
    if (selected.length === 0 || downloadable.length === 0) return null;
    const optionKey = selected
      .map(option => `${option.sno}:${option.purchase_count ?? 1}`)
      .join(',');
    const couponKey = downloadable
      .map(coupon => coupon.item.sno)
      .sort((a, b) => a - b)
      .join(',');
    return `${optionKey}|${couponKey}`;
  }, [downloadable, selected]);

  useEffect(() => {
    if (mode !== 'variantC' || isAnonymousUser || downloadable.length === 0) {
      setIsAutoRedeeming(false);
    }
  }, [downloadable.length, isAnonymousUser, mode]);

  useEffect(() => {
    if (mode !== 'variantC') return;
    if (isAnonymousUser) return;
    if (isPending || isAutoRedeeming) return;
    if (!pricing) return;
    if (downloadable.length === 0) return;
    if (redeemKey === null) return;
    if (attemptedKeysRef.current.has(redeemKey)) return;
    attemptedKeysRef.current.add(redeemKey);
    setIsAutoRedeeming(true);
    postBatchRedeemCoupon()
      .then(() => {
        downloadable.forEach(coupon => {
          logCouponDownloadComplete({ coupon, isDownloadAll: true });
        });
        bridge.toast('최대 할인 쿠폰이 자동으로 적용되었어요');
      })
      .catch(() => {})
      .finally(() => {
        setIsAutoRedeeming(false);
      });
  }, [
    downloadable,
    isAnonymousUser,
    isAutoRedeeming,
    isPending,
    logCouponDownloadComplete,
    mode,
    postBatchRedeemCoupon,
    pricing,
    redeemKey,
  ]);

  return { isAutoRedeeming: isAutoRedeeming || isPending };
};
